import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Calendar } from 'lucide-react';
import { sendMessage, getChatHistory } from '../../lib/api';
import { v4 as uuidv4 } from 'uuid';

const PABLO_WHATSAPP = process.env.REACT_APP_PABLO_WHATSAPP || '523329186014';
const CALENDLY_URL = 'https://calendly.com/hola-pablolopezasesor/vidamujer';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [finalMessage, setFinalMessage] = useState('');
  const [leadSaved, setLeadSaved] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [isCalendlyQualified, setIsCalendlyQualified] = useState(false);
  const [isNewsletterLead, setIsNewsletterLead] = useState(false);
  const [hasTrackedVisit, setHasTrackedVisit] = useState(false);
  const [hasTrackedChatOpen, setHasTrackedChatOpen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);  // Track if user clicked a button
  const [showWelcomeButtons, setShowWelcomeButtons] = useState(true);  // Show initial buttons
  const [wantsNewsletter, setWantsNewsletter] = useState(true);
  const [leadData, setLeadData] = useState({
    name: '',
    age: '',
    profile: '',
    income: '',
    suggestedSum: '',
    capacity: '',
    plazo: ''
  });
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('vida_mujer_session');
    if (stored) return stored;
    const newId = uuidv4();
    localStorage.setItem('vida_mujer_session', newId);
    return newId;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastMessageRef = useRef(null);

  // Track page visit on mount
  useEffect(() => {
    if (!hasTrackedVisit) {
      trackPageVisit();
      setHasTrackedVisit(true);
    }
  }, [hasTrackedVisit]);

  // AUTO-OPEN CHAT - DISABLED for better conversion
  // Chat now opens only when user clicks CTA button
  // useEffect(() => {
  //   const autoOpenTimer = setTimeout(() => {
  //     const hasSeenChat = sessionStorage.getItem('chat_auto_opened');
  //     if (!hasSeenChat && !isOpen) {
  //       setIsOpen(true);
  //       sessionStorage.setItem('chat_auto_opened', 'true');
  //     }
  //   }, 800);
  //   return () => clearTimeout(autoOpenTimer);
  // }, []);

  // Track page visit to backend
  const trackPageVisit = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/analytics/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch (error) {
      console.error('Error tracking visit:', error);
    }
  };

  // Track Meta Pixel events
  const trackMetaPixel = (eventName, params = {}) => {
    if (typeof window.trackMetaEvent === 'function') {
      window.trackMetaEvent(eventName, params);
    }
  };

  // Scroll to show the beginning of the last assistant message
  const scrollToLastMessage = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Only scroll when a new assistant message arrives
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      // Small delay to ensure the DOM has updated
      setTimeout(() => scrollToLastMessage(), 100);
    }
  }, [messages]);

  // Track when chat is opened for the first time
  // NOTE: We no longer track "Lead" here - only when user clicks a button
  useEffect(() => {
    if (isOpen && !hasTrackedChatOpen) {
      setHasTrackedChatOpen(true);
      // We DON'T track Lead event here anymore
      // Lead event is only tracked when user clicks a welcome button
    }
  }, [isOpen, hasTrackedChatOpen]);

  const trackChatOpened = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/analytics/chat-opened`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch (error) {
      console.error('Error tracking chat opened:', error);
    }
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen]);

  // Extract lead data from conversation
  const extractLeadData = (allMessages) => {
    const conversationText = allMessages.map(m => m.content).join(' ').toLowerCase();
    const data = { ...leadData };
    
    // FIRST: Try to extract name from AI responses (most reliable)
    // The AI always addresses the user by name
    for (const msg of allMessages) {
      if (msg.role === 'assistant' && !data.name) {
        const content = msg.content;
        
        // Pattern: "¡Hola [Nombre]!" or "Hola, [Nombre]"
        let nameMatch = content.match(/[¡!]?[Hh]ola[,\s]+([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+)[!,\s]/);
        if (nameMatch && !['Soy', 'Me', 'Mucho', 'Bienvenida'].includes(nameMatch[1])) {
          data.name = nameMatch[1];
          continue;
        }
        
        // Pattern: "perfectamente, [Nombre]" or "entiendo, [Nombre]"
        nameMatch = content.match(/(?:perfectamente|entiendo|excelente|maravilloso)[,!\s]+([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+)[!,\s.]/i);
        if (nameMatch) {
          data.name = nameMatch[1];
          continue;
        }
        
        // Pattern: "[Nombre], tu diagnóstico" or "[Nombre], te recomiendo"
        nameMatch = content.match(/^([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+),\s+(?:tu|te|como|para|en)/i);
        if (nameMatch && nameMatch[1].length > 2 && nameMatch[1].length < 20) {
          data.name = nameMatch[1];
          continue;
        }
        
        // Pattern: "para ti, [Nombre]" or "¿verdad, [Nombre]?"
        nameMatch = content.match(/(?:para ti|verdad|cierto)[,\s]+([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+)[?!,\s]/i);
        if (nameMatch) {
          data.name = nameMatch[1];
          continue;
        }
      }
    }
    
    // SECOND: Try to extract from user messages if not found in AI responses
    for (const msg of allMessages) {
      if (msg.role === 'user') {
        const content = msg.content.trim();
        
        // Extract name using multiple patterns
        if (!data.name) {
          // Pattern 1: "soy [Nombre]" or "me llamo [Nombre]"
          const nameMatch = content.match(/(?:soy|me llamo|mi nombre es)\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+)/i);
          if (nameMatch) {
            data.name = nameMatch[1];
          }
          // Pattern 2: Short response (likely just a name) early in conversation
          else if (content.split(' ').length <= 3 && !content.includes('$') && !content.includes('%') && allMessages.indexOf(msg) < 4) {
            const cleanName = content.replace(/^(hola[,\s]*|me llamo[,\s]*|soy[,\s]*)/i, '').trim();
            if (cleanName && cleanName.length > 2 && cleanName.length < 20 && /^[A-ZÁÉÍÓÚÑ]/i.test(cleanName)) {
              data.name = cleanName.split(/[\s,]/)[0];
            }
          }
        }
        
        // Extract age
        const ageMatch = content.match(/(\d{2})\s*(años|año)/i) || content.match(/tengo\s*(\d{2})/i);
        if (ageMatch) {
          data.age = ageMatch[1];
        }
        // Extract income
        const incomeMatch = content.match(/(?:gano|ingreso|sueldo)[^\d]*(\d[\d,]*)/i);
        if (incomeMatch && parseInt(incomeMatch[1].replace(/,/g, '')) > 5000) {
          data.income = incomeMatch[1].replace(/,/g, '');
        } else {
          // Fallback: any number > 10000 that looks like income
          const numMatch = content.match(/\$?\s*([\d,]+)\s*(?:pesos|mxn|mensuales)?/i);
          if (numMatch && parseInt(numMatch[1].replace(/,/g, '')) > 10000) {
            data.income = numMatch[1].replace(/,/g, '');
          }
        }
        
        // Extract capacity answer (pregunta de capacidad)
        if (content.toLowerCase().includes('menos de $3,000') || (content.includes('a)') && content.toLowerCase().includes('3,000'))) {
          data.capacity = 'a) Menos de $3,000 MXN';
        } else if (content.toLowerCase().includes('entre $3,000') || content.toLowerCase().includes('3,000 y 6,999') || (content.includes('b)') && content.toLowerCase().includes('3,000'))) {
          data.capacity = 'b) Entre $3,000 y $6,999 MXN';
        } else if (content.toLowerCase().includes('$7,000') || content.toLowerCase().includes('7,000 o más') || content.includes('c)')) {
          data.capacity = 'c) $7,000 MXN o más';
        }
        
        // Extract plazo answer (pregunta de plazo)
        if (content.toLowerCase().includes('antes posible') || content.toLowerCase().includes('este mes') || (content.includes('a)') && content.toLowerCase().includes('posible'))) {
          data.plazo = 'a) Lo antes posible / Este mes';
        } else if (content.toLowerCase().includes('próximos 3 meses') || content.toLowerCase().includes('3 meses') || (content.includes('b)') && content.toLowerCase().includes('meses'))) {
          data.plazo = 'b) En los próximos 3 meses';
        } else if (content.toLowerCase().includes('explorando') || content.toLowerCase().includes('largo plazo') || (content.includes('c)') && content.toLowerCase().includes('explor'))) {
          data.plazo = 'c) Solo estoy explorando a largo plazo';
        }
      }
      // Extract profile and suggested sum from assistant responses
      if (msg.role === 'assistant') {
        if (msg.content.toLowerCase().includes('metas') && !data.profile) data.profile = 'Metas';
        if (msg.content.toLowerCase().includes('salud') && !data.profile) data.profile = 'Salud';
        if (msg.content.toLowerCase().includes('estabilidad') && !data.profile) data.profile = 'Estabilidad';
        
        const sumMatch = msg.content.match(/([\d,]+)\s*UDIS/i);
        if (sumMatch) {
          data.suggestedSum = sumMatch[1];
        }
      }
    }
    
    // Log for debugging
    console.log('Extracted lead data:', data);
    
    return data;
  };

  const loadHistory = async () => {
    try {
      const history = await getChatHistory(sessionId);
      if (history.messages && history.messages.length > 0) {
        const loadedMessages = history.messages.map(msg => ({
          role: msg.role,
          content: msg.content
            .replace(/\[\[WHATSAPP_READY\]\]/g, '')
            .replace(/\[\[CALENDLY_QUALIFIED\]\]/g, '')
            .replace(/\[\[NEWSLETTER_INVITE\]\]/g, '')
        }));
        setMessages(loadedMessages);
        
        // Check if any closing marker should be shown
        const lastAssistantMsg = history.messages.filter(m => m.role === 'assistant').pop();
        if (lastAssistantMsg) {
          const cleanContent = lastAssistantMsg.content
            .replace(/\[\[CALENDLY_QUALIFIED\]\]/g, '')
            .replace(/\[\[WHATSAPP_READY\]\]/g, '')
            .replace(/\[\[NEWSLETTER_INVITE\]\]/g, '');
          
          // Check for Calendly qualified marker
          if (lastAssistantMsg.content.includes('[[CALENDLY_QUALIFIED]]')) {
            setFinalMessage(cleanContent);
            setLeadData(extractLeadData(loadedMessages));
            setIsCalendlyQualified(true);
            setIsNewsletterLead(false);
            const savedPhone = localStorage.getItem('vida_mujer_phone');
            if (savedPhone) {
              setPhoneNumber(savedPhone);
              setShowWhatsAppModal(true);
            } else {
              setShowPhoneModal(true);
            }
          }
          // Check for WhatsApp marker
          else if (lastAssistantMsg.content.includes('[[WHATSAPP_READY]]')) {
            setFinalMessage(cleanContent);
            setLeadData(extractLeadData(loadedMessages));
            setIsCalendlyQualified(false);
            setIsNewsletterLead(false);
            const savedPhone = localStorage.getItem('vida_mujer_phone');
            if (savedPhone) {
              setPhoneNumber(savedPhone);
              setShowWhatsAppModal(true);
            } else {
              setShowPhoneModal(true);
            }
          }
          // Check for Newsletter marker (unqualified lead)
          else if (lastAssistantMsg.content.includes('[[NEWSLETTER_INVITE]]')) {
            setFinalMessage(cleanContent);
            setLeadData(extractLeadData(loadedMessages));
            setIsNewsletterLead(true);
            setShowNewsletterModal(true);
          }
        }
      } else {
        // New welcome flow with quick-reply buttons
        setMessages([{
          role: 'assistant',
          content: '¡Hola! Qué gusto que quieras asegurar tu futuro financiero. 🛡️\n\n¿Te gustaría descubrir en **60 segundos** cómo recuperar el **115%** de tus ahorros mientras te proteges?'
        }]);
        setShowWelcomeButtons(true);
        setHasUserInteracted(false);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      setMessages([{
        role: 'assistant',
        content: '¡Hola! Qué gusto que quieras asegurar tu futuro financiero. 🛡️\n\n¿Te gustaría descubrir en **60 segundos** cómo recuperar el **115%** de tus ahorros mientras te proteges?'
      }]);
      setShowWelcomeButtons(true);
      setHasUserInteracted(false);
    }
  };

  // Handle welcome button click - this starts the real conversation
  const handleWelcomeButtonClick = async (buttonText) => {
    setShowWelcomeButtons(false);
    setHasUserInteracted(true);
    
    // Track this as the REAL "Inició chat" event
    trackMetaPixel('Lead', { content_name: 'Chat Started - Button Click' });
    
    // Track step in backend
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads/track-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, step: 'inicio_confirmado' })
      });
    } catch (error) {
      console.error('Error tracking step:', error);
    }
    
    // Add user's button click as a message
    const userMessage = buttonText;
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);
    
    try {
      // Send a special message to get the name question
      const response = await sendMessage(sessionId, userMessage);
      let assistantContent = response.response;
      
      // If AI doesn't ask for name, we force it
      if (!assistantContent.toLowerCase().includes('nombre')) {
        assistantContent = '¡Excelente decisión! 🎉\n\nAntes de calcular tus beneficios personalizados, **¿con qué nombre tengo el gusto de dirigirme a ti?**';
      }
      
      const displayContent = assistantContent
        .replace(/\[\[CALENDLY_QUALIFIED\]\]/g, '')
        .replace(/\[\[WHATSAPP_READY\]\]/g, '')
        .replace(/\[\[NEWSLETTER_INVITE\]\]/g, '');
      
      setMessages([...newMessages, { role: 'assistant', content: displayContent }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: '¡Excelente decisión! 🎉\n\nAntes de calcular tus beneficios personalizados, **¿con qué nombre tengo el gusto de dirigirme a ti?**' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const response = await sendMessage(sessionId, userMessage);
      let assistantContent = response.response;
      
      // Clean the content for display (remove all markers)
      let displayContent = assistantContent
        .replace(/\[\[CALENDLY_QUALIFIED\]\]/g, '')
        .replace(/\[\[WHATSAPP_READY\]\]/g, '')
        .replace(/\[\[NEWSLETTER_INVITE\]\]/g, '');
      
      // Check for CALENDLY marker (high-potential lead)
      if (assistantContent.includes('[[CALENDLY_QUALIFIED]]')) {
        setFinalMessage(displayContent);
        const allMsgs = [...newMessages, { role: 'assistant', content: displayContent }];
        setLeadData(extractLeadData(allMsgs));
        setIsCalendlyQualified(true);
        setIsNewsletterLead(false);
        setShowPhoneModal(true);
      }
      // Check for WhatsApp marker (standard lead)
      else if (assistantContent.includes('[[WHATSAPP_READY]]')) {
        setFinalMessage(displayContent);
        const allMsgs = [...newMessages, { role: 'assistant', content: displayContent }];
        setLeadData(extractLeadData(allMsgs));
        setIsCalendlyQualified(false);
        setIsNewsletterLead(false);
        setShowPhoneModal(true);
      }
      // Check for Newsletter marker (unqualified lead - low capacity)
      else if (assistantContent.includes('[[NEWSLETTER_INVITE]]')) {
        setFinalMessage(displayContent);
        const allMsgs = [...newMessages, { role: 'assistant', content: displayContent }];
        setLeadData(extractLeadData(allMsgs));
        setIsNewsletterLead(true);
        setShowNewsletterModal(true);
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: displayContent }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Disculpa, hubo un problema. Por favor, inténtalo de nuevo.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateWhatsAppMessage = () => {
    const data = extractLeadData(messages);
    
    const message = `¡Hola Pablo! 👋

Vengo del asistente de Vida Mujer y me gustaría agendar una cita contigo.

📋 *MI INFORMACIÓN:*
• Nombre: ${data.name || 'No proporcionado'}
• Edad: ${data.age ? data.age + ' años' : 'No proporcionada'}
• Perfil de interés: ${data.profile || 'Por definir'}
• Ingreso mensual: ${data.income ? '$' + parseInt(data.income).toLocaleString() + ' MXN' : 'No proporcionado'}
• Suma sugerida: ${data.suggestedSum ? data.suggestedSum + ' UDIS' : 'Por definir'}

¿Cuándo podríamos hablar?`;

    return encodeURIComponent(message);
  };

  const handleWhatsAppClick = () => {
    // Track WhatsApp click
    trackAction('whatsapp_click');
    
    // Track CompleteRegistration in Meta Pixel
    trackMetaPixel('CompleteRegistration', { 
      content_name: 'WhatsApp Contact',
      status: 'converted'
    });
    
    // Update lead status to converted
    markLeadAsConverted();
    
    const message = generateWhatsAppMessage();
    const url = `https://wa.me/${PABLO_WHATSAPP}?text=${message}`;
    window.open(url, '_blank');
  };

  const markLeadAsConverted = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads/mark-converted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch (error) {
      console.error('Error marking lead as converted:', error);
    }
  };

  const handleNewsletterSubmit = async () => {
    if (!newsletterEmail.trim() || !newsletterEmail.includes('@')) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const data = extractLeadData(messages);
      
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterEmail,
          name: data.name || 'Sin nombre',
          source: 'chatbot_unqualified'
        })
      });
      
      // Show success message
      setShowNewsletterModal(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `¡Perfecto! Te he suscrito al Newsletter de Mujeres Líderes. Pronto recibirás contenido exclusivo de Pablo López en tu correo **${newsletterEmail}**. ¡Gracias por tu interés! 💜`
      }]);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
    }
  };

  const handlePhoneSubmit = async () => {
    if (!phoneNumber.trim() || phoneNumber.length < 10) return;
    
    // Save phone to localStorage
    localStorage.setItem('vida_mujer_phone', phoneNumber);
    
    // Save email if provided
    if (wantsNewsletter && leadEmail && leadEmail.includes('@')) {
      localStorage.setItem('vida_mujer_email', leadEmail);
    }
    
    // Close phone modal, show WhatsApp modal
    setShowPhoneModal(false);
    setShowWhatsAppModal(true);
    
    // Start PDF generation and lead notification in parallel
    setPdfLoading(true);
    
    // Save lead and notify Pablo
    await saveLeadAndNotify();
    
    // Generate PDF
    await generatePdf();
    
    // Subscribe to newsletter if email provided
    if (wantsNewsletter && leadEmail && leadEmail.includes('@')) {
      await subscribeLeadToNewsletter();
    }
  };

  const subscribeLeadToNewsletter = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const data = extractLeadData(messages);
      
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: leadEmail,
          name: data.name || 'Lead',
          source: 'phone_modal_optin',
          phone: phoneNumber
        })
      });
      
      console.log('Lead subscribed to newsletter:', leadEmail);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
    }
  };

  const generatePdf = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const data = extractLeadData(messages);
      
      const response = await fetch(`${API_URL}/api/pdf/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          lead_name: data.name || 'Sin nombre',
          lead_phone: phoneNumber,
          age: data.age ? parseInt(data.age) : null,
          profile_type: data.profile || 'No definido',
          monthly_income: data.income ? parseFloat(data.income) : null,
          recommended_suma: data.suggestedSum ? parseInt(data.suggestedSum.replace(/,/g, '')) : null,
          diagnosis: finalMessage,
          lead_capacity: data.capacity || null,
          lead_plazo: data.plazo || null
        })
      });
      
      const result = await response.json();
      console.log('PDF generation result:', result);
      
      if (result.status === 'success' && result.pdf_url) {
        setPdfUrl(result.pdf_url);
      } else {
        // PDF generation failed or async - show fallback message
        console.log('PDF not immediately available:', result);
        setPdfUrl('error');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfUrl('error');
    } finally {
      setPdfLoading(false);
    }
  };

  const saveLeadAndNotify = async () => {
    if (leadSaved) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const data = extractLeadData(messages);
      
      // Save lead and send notification to Pablo with full diagnosis
      await fetch(`${API_URL}/api/leads/qualified-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          lead_name: data.name || 'Sin nombre',
          lead_phone: phoneNumber,
          lead_email: (wantsNewsletter && leadEmail) ? leadEmail : null,
          age: data.age ? parseInt(data.age) : null,
          profile_type: data.profile || 'No definido',
          monthly_income: data.income ? parseFloat(data.income) : null,
          recommended_suma: data.suggestedSum ? parseInt(data.suggestedSum.replace(/,/g, '')) : null,
          diagnosis_text: finalMessage || '',  // Full diagnosis text
          lead_capacity: data.capacity || null,
          lead_plazo: data.plazo || null
        })
      });
      
      setLeadSaved(true);
    } catch (error) {
      console.error('Error saving lead:', error);
    }
  };

  // Track lead actions (PDF download, WhatsApp click)
  const trackAction = async (action) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads/track-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          action: action
        })
      });
      console.log(`Tracked action: ${action}`);
    } catch (error) {
      console.error('Error tracking action:', error);
    }
  };

  // Subscribe to newsletter from the main modal
  const handleModalNewsletterSubscribe = async () => {
    if (!newsletterEmail || !newsletterEmail.includes('@')) return;
    
    setNewsletterSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const data = extractLeadData(messages);
      
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterEmail,
          name: data.name || 'Lead',
          source: 'post_pdf_download'
        })
      });
      
      setNewsletterSubmitted(true);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Reset conversation and start fresh
  const resetConversation = () => {
    // Clear session from localStorage
    localStorage.removeItem('vida_mujer_session');
    localStorage.removeItem('vida_mujer_phone');
    
    // Generate new session ID
    const newSessionId = uuidv4();
    localStorage.setItem('vida_mujer_session', newSessionId);
    
    // Reset all states
    setMessages([{
      role: 'assistant',
      content: '¡Hola! Soy el asistente de Pablo López, Asesor Patrimonial especializado en Vida Mujer de Seguros Monterrey New York Life.\n\nAntes de comenzar, **¿cuál es tu nombre?** Me encantaría dirigirme a ti de manera personalizada.'
    }]);
    setShowPhoneModal(false);
    setShowWhatsAppModal(false);
    setPhoneNumber('');
    setFinalMessage('');
    setLeadSaved(false);
    setPdfUrl(null);
    setPdfLoading(false);
    setIsCalendlyQualified(false);
    setLeadData({ name: '', age: '', profile: '', income: '', suggestedSum: '', capacity: '', plazo: '' });
    
    // Reload page to get new session ID
    window.location.reload();
  };

  const formatMessage = (content) => {
    // Remove ALL markers
    let cleanContent = content
      .replace(/\[\[WHATSAPP_READY\]\]/g, '')
      .replace(/\[\[CALENDLY_QUALIFIED\]\]/g, '');
    
    return cleanContent.split('\n').map((line, i) => {
      // Handle headers (## and ###)
      if (line.startsWith('### ')) {
        const headerText = line.replace('### ', '');
        return (
          <h4 key={i} className="font-bold text-sm mt-3 mb-1 text-[#0F2B5B]">
            {formatBoldText(headerText)}
          </h4>
        );
      }
      if (line.startsWith('## ')) {
        const headerText = line.replace('## ', '');
        return (
          <h3 key={i} className="font-bold text-base mt-4 mb-2 text-[#0F2B5B]">
            {formatBoldText(headerText)}
          </h3>
        );
      }
      
      // Handle horizontal rules
      if (line.trim() === '---') {
        return <hr key={i} className="my-3 border-stone-200" />;
      }
      
      // Regular text with bold formatting
      return (
        <span key={i}>
          {formatBoldText(line)}
          {i < cleanContent.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  // Helper function to format bold text
  const formatBoldText = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, j) => 
      j % 2 === 1 ? <strong key={j}>{part}</strong> : part
    );
  };

  return (
    <>
      {/* Chat Trigger Button - positioned well above Emergent badge */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            data-testid="chat-trigger-btn"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-28 right-4 md:bottom-20 md:right-8 z-[9999] h-14 w-14 md:h-16 md:w-16 rounded-full bg-[#0F2B5B] text-white shadow-2xl flex items-center justify-center hover:shadow-3xl transition-shadow"
          >
            <MessageCircle size={24} className="md:hidden" />
            <MessageCircle size={28} className="hidden md:block" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 md:inset-auto md:bottom-20 md:right-8 z-[9999] w-full md:w-[400px] h-full md:h-auto md:max-h-[580px] bg-white shadow-2xl md:rounded-2xl border-0 md:border border-stone-100 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#0F2B5B] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_womenlife-expert/artifacts/33njuh6q_Logo%20PL.png" 
                  alt="Pablo López" 
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-semibold text-sm">Asistente de Pablo López</h3>
                  <p className="text-xs text-white/70">Asesor Patrimonial</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={resetConversation}
                  className="hover:bg-white/10 p-2 rounded-full transition-colors text-xs"
                  title="Nueva conversación"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                    <path d="M3 3v5h5"/>
                  </svg>
                </button>
                <button
                  data-testid="chat-close-btn"
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/10 p-2 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 chat-scroll bg-stone-50" style={{ minHeight: '300px' }}>
              {messages.map((msg, index) => {
                const isLastAssistantMessage = msg.role === 'assistant' && index === messages.length - 1;
                return (
                  <motion.div
                    key={index}
                    ref={isLastAssistantMessage ? lastMessageRef : null}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#0F2B5B] text-white rounded-br-md'
                          : 'bg-white text-stone-800 border border-stone-200 rounded-bl-md shadow-sm'
                      }`}
                    >
                      {formatMessage(msg.content)}
                    </div>
                  </motion.div>
                );
              })}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white border border-stone-200 p-3 rounded-2xl rounded-bl-md shadow-sm">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0F2B5B]" />
                  </div>
                </motion.div>
              )}
              
              {/* Welcome Quick-Reply Buttons */}
              {showWelcomeButtons && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col gap-2 mt-3"
                >
                  <button
                    onClick={() => handleWelcomeButtonClick('¡Sí, muéstrame cómo!')}
                    className="w-full py-3 px-4 rounded-xl bg-[#0F2B5B] text-white font-medium hover:bg-[#0F2B5B]/90 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    ✨ ¡Sí, muéstrame cómo!
                  </button>
                  <button
                    onClick={() => handleWelcomeButtonClick('Quiero mi diagnóstico')}
                    className="w-full py-3 px-4 rounded-xl border-2 border-[#0F2B5B] text-[#0F2B5B] font-medium hover:bg-[#E5D4CE]/30 transition-all text-sm"
                  >
                    📊 Quiero mi diagnóstico
                  </button>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input - Only show after user has interacted */}
            {(hasUserInteracted || messages.length > 1) && (
            <div className="p-4 pb-16 md:pb-4 border-t border-stone-100 bg-white">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  data-testid="chat-input"
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-[#0F2B5B]/20 focus:border-[#0F2B5B] text-sm transition-all"
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  className="p-3 rounded-xl bg-[#0F2B5B] text-white hover:bg-[#0F2B5B]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Send size={18} />
                </button>
              </div>
              <p className="text-[10px] text-stone-400 mt-2 text-center">
                Asistente IA • Para cotizaciones exactas, agenda cita con Pablo
              </p>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phone Number Modal */}
      <AnimatePresence>
        {showPhoneModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-[#0F2B5B] text-white p-5 text-center">
                <div className="w-16 h-16 rounded-full bg-[#E5D4CE] flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#0F2B5B] font-bold text-2xl">📱</span>
                </div>
                <h3 className="font-semibold text-lg">¡Un último paso!</h3>
                <p className="text-sm text-white/70 mt-1">Para enviarte tu diagnóstico personalizado</p>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Tu número de WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Ej: 3312345678"
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-[#0F2B5B]/20 focus:border-[#0F2B5B] text-lg text-center tracking-wider"
                    maxLength={10}
                  />
                  <p className="text-xs text-stone-400 mt-2 text-center">
                    Solo 10 dígitos, sin espacios ni guiones
                  </p>
                </div>

                {/* Email field for newsletter */}
                <div className="pt-2 border-t border-stone-100">
                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={wantsNewsletter}
                      onChange={(e) => setWantsNewsletter(e.target.checked)}
                      className="w-4 h-4 rounded border-stone-300 text-[#0F2B5B] focus:ring-[#0F2B5B]"
                    />
                    <span className="text-sm text-stone-600">
                      Recibir mi diagnóstico por email + tips financieros
                    </span>
                  </label>
                  {wantsNewsletter && (
                    <motion.input
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      type="email"
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-[#0F2B5B]/20 focus:border-[#0F2B5B] text-center"
                    />
                  )}
                </div>

                <button
                  onClick={handlePhoneSubmit}
                  disabled={phoneNumber.length < 10}
                  className="w-full py-4 rounded-xl bg-[#0F2B5B] text-white font-semibold hover:bg-[#0F2B5B]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Ver mi diagnóstico
                </button>

                <p className="text-[10px] text-stone-400 text-center">
                  🔒 Tu información está segura y no será compartida
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Modal Popup */}
      <AnimatePresence>
        {showWhatsAppModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowWhatsAppModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-[#0F2B5B] text-white p-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_womenlife-expert/artifacts/33njuh6q_Logo%20PL.png" 
                    alt="Pablo López" 
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-sm">¡Tu diagnóstico está listo!</h3>
                    <p className="text-xs text-white/70">
                      {isCalendlyQualified ? 'Descárgalo y agenda tu sesión prioritaria' : 'Descárgalo y agenda tu cita'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="hover:bg-white/10 p-2 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto p-6 pb-2">
                {/* Modal Content - Diagnosis */}
                <div className="text-sm text-stone-700 leading-relaxed max-h-[25vh] overflow-y-auto mb-4">
                  {formatMessage(finalMessage)}
                </div>

              {/* Modal Footer with PDF + Calendly/WhatsApp Buttons */}
              <div className="space-y-3 pb-4">
                {/* PDF Download Button */}
                {pdfLoading ? (
                  <div className="w-full py-4 rounded-xl bg-stone-100 text-stone-500 font-medium flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generando tu diagnóstico PDF...
                  </div>
                ) : pdfUrl && pdfUrl !== 'error' ? (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackAction('pdf_download');
                      setPdfDownloaded(true);
                    }}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#0F2B5B] hover:bg-[#0F2B5B]/90 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z" />
                    </svg>
                    Descargar Diagnóstico PDF
                  </a>
                ) : pdfUrl === 'error' ? (
                  <div className="w-full py-4 rounded-xl bg-green-50 text-green-700 font-medium text-center text-sm">
                    ✅ Tu diagnóstico se está generando. Pablo te lo enviará por WhatsApp o email en breve.
                  </div>
                ) : (
                  <div className="w-full py-4 rounded-xl bg-stone-50 text-stone-500 font-medium text-center text-sm">
                    Preparando tu diagnóstico...
                  </div>
                )}

                {/* Urgency message */}
                <p className="text-xs text-orange-600 font-medium flex items-center justify-center gap-1">
                  <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                  {isCalendlyQualified 
                    ? '¡Felicidades! Calificas para una sesión de estrategia prioritaria' 
                    : 'Pablo tiene disponibilidad limitada esta semana'}
                </p>
                
                {/* CALENDLY BUTTON - Only for qualified leads */}
                {isCalendlyQualified ? (
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="calendly-btn"
                    className="relative z-10 w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-[#006BFF] to-[#0052CC] hover:from-[#0052CC] hover:to-[#003D99] text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    <Calendar className="w-6 h-6" />
                    <span>Agendar Sesión Prioritaria</span>
                  </a>
                ) : (
                  /* WHATSAPP BUTTON - For standard leads */
                  <button
                    data-testid="whatsapp-btn"
                    onClick={handleWhatsAppClick}
                    className="relative w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span>Agendar cita con Pablo</span>
                  </button>
                )}
                
                {/* Secondary WhatsApp option for Calendly-qualified leads */}
                {isCalendlyQualified && (
                  <button
                    data-testid="whatsapp-btn-secondary"
                    onClick={handleWhatsAppClick}
                    className="relative z-10 w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium rounded-xl transition-all text-sm"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    O contactar por WhatsApp
                  </button>
                )}
                
                {/* Newsletter subscription - Shows only after PDF download */}
                {pdfDownloaded && (
                  <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border border-purple-100">
                    {newsletterSubmitted ? (
                      <div className="text-center py-2">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-sm text-green-700 font-medium">¡Te has suscrito exitosamente!</p>
                        <p className="text-xs text-green-600 mt-1">Recibirás contenido exclusivo en tu correo.</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect width="20" height="16" x="2" y="4" rx="2"/>
                              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-purple-900">Newsletter Mujeres Líderes</p>
                            <p className="text-[10px] text-purple-600">Tips financieros y contenido exclusivo</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={newsletterEmail}
                            onChange={(e) => setNewsletterEmail(e.target.value)}
                            placeholder="Tu correo electrónico"
                            className="flex-1 px-3 py-2 text-sm rounded-xl border border-purple-200 focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none"
                          />
                          <button
                            onClick={handleModalNewsletterSubscribe}
                            disabled={newsletterSubmitting || !newsletterEmail.includes('@')}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-sm font-medium rounded-xl transition-all flex items-center gap-1"
                          >
                            {newsletterSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                                </svg>
                                Suscribirse
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                <p className="text-[10px] text-stone-400 text-center pt-2 pb-2">Sin compromiso • Consulta gratuita</p>
              </div>
              </div> {/* End of scrollable area */}
            </motion.div>
          </motion.div>
        )}

        {/* Newsletter Modal - For unqualified leads */}
        {showNewsletterModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowNewsletterModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            >
              <div className="bg-gradient-to-r from-[#9333EA] to-[#7C3AED] text-white p-6">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  Newsletter Mujeres Líderes
                </h3>
                <p className="text-white/80 text-sm mt-1">Contenido exclusivo de Pablo López</p>
              </div>
              
              <div className="p-6">
                <p className="text-stone-600 text-sm mb-4">
                  Recibe estrategias de ahorro, tips financieros y contenido exclusivo para mujeres profesionales.
                </p>
                
                <div className="space-y-3">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#9333EA] focus:ring-2 focus:ring-[#9333EA]/20 outline-none transition-all"
                  />
                  
                  <button
                    onClick={handleNewsletterSubmit}
                    disabled={!newsletterEmail.includes('@')}
                    className="w-full py-3 bg-gradient-to-r from-[#9333EA] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suscribirme al Newsletter
                  </button>
                  
                  <button
                    onClick={() => setShowNewsletterModal(false)}
                    className="w-full py-2 text-stone-500 hover:text-stone-700 text-sm transition-colors"
                  >
                    Ahora no, gracias
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
